/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { EllipsesAnimation } from '../../primitives/ellipses-animation/EllipsesAnimation';
import { LazyAnimatedRovoIcon } from '../../primitives/rovo-animated-icon/LazyAnimatedRovoIcon';

import { MorphingLoader } from './MorphingLoader';

const styles = cssMap({
	root: {
		height: '24px',
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'left',
		flexWrap: 'wrap',
		alignItems: 'center',
	},
	iconContainer: {
		marginRight: token('space.025'),
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	text: {
		color: token('color.text'),
		font: token('font.body'),
		wordBreak: 'break-word',
		flex: 1,
	},
	ellipsesContainer: {
		flex: 1,
	},
});

type RovoThinkingIndicatorProps = {
	// text shown between the Rovo icon and ellipses
	title: string;
	isAnimated?: boolean;
	// true = Rovo icon, ReactNode support to enable passing Agent icons
	icon?: boolean | ReactNode;
	// whether to apply shimmer and loading icon animation effect to the title text
	animation?: boolean;
	// whether to show the ellipsis dots (default: true)
	showEllipsis?: boolean;
	testId?: string;
};

const getLoadingMorphingIcon = () => {
	return <MorphingLoader testId="morphing-loader" />;
};

const getIcon = (icon: ReactNode | boolean): ReactNode => {
	if (React.isValidElement(icon)) {
		return icon;
	} else if (icon) {
		return (
			<LazyAnimatedRovoIcon alt="Rovo" testId="rovo-loading-animation" width="24px" height="24px" />
		);
	}

	return null;
};

export const RovoThinkingIndicator = ({
	title,
	isAnimated = true,
	icon = true,
	animation = false,
	showEllipsis = true,
	testId,
}: RovoThinkingIndicatorProps): JSX.Element => {
	const enableAnimation =
		animation &&
		FeatureGates.getExperimentValue('rovo_chat_thinking_traces_improvements', 'isEnabled', false);
	return (
		<Box xcss={styles.root} testId={testId}>
			<Box xcss={styles.iconContainer}>
				{enableAnimation ? getLoadingMorphingIcon() : getIcon(icon)}
				<Box xcss={styles.text}>{enableAnimation ? <ShimmerText>{title}</ShimmerText> : title}</Box>
			</Box>
			<Box xcss={styles.ellipsesContainer}>
				{showEllipsis && <EllipsesAnimation isAnimated={isAnimated} />}
			</Box>
		</Box>
	);
};
